import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import VideoCaptionTrack from "./videoCaptionTrack";

// Single shared asset renderer used by the admin preview AND the learner portal
// — never a second rendering system. Renders one asset by type:
//   video | pdf | image | external_url | rich_text
// Uses the asset's signed `url` (preview/learner endpoints sign it). rich_text
// is sanitised before render.
//   onAssetEvent({ assetId, completed })  — fired when the asset is viewed/ended
//   onProgress({ assetId, watchedSeconds, durationSeconds }) — throttled (~10s)
//     video time reporting for learner watched-seconds tracking (optional).
const LmsAssetRenderer = ({
  asset,
  onAssetEvent,
  onProgress,
  resumeSeconds = 0,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  const type = asset?.type;
  const src = asset?.signedUrl || asset?.previewUrl || asset?.fileUrl || asset?.url;
  const lastReport = useRef(0);
  const videoRef = useRef(null);
  const didResume = useRef(false);
  const [pdfPreviewBusy, setPdfPreviewBusy] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState(false);

  const fireComplete = () => onAssetEvent?.({ assetId: asset?._id, completed: true });
  const isDirectUrl = (value = "") =>
    /^(https?:|blob:|data:)/i.test(value) || value.startsWith("/");
  const firstDirectUrl = (...values) =>
    values.map((value) => String(value || "")).find((value) => value && isDirectUrl(value)) || "";
  const withPdfControls = (value = "") =>
    value && !value.includes("#") ? `${value}#toolbar=0&navpanes=0` : value;

  useEffect(() => {
    didResume.current = false;
  }, [asset?._id, resumeSeconds]);

  const pdfBaseSrc =
    type === "pdf"
      ? firstDirectUrl(asset?.previewUrl, asset?.signedUrl, asset?.fileUrl, asset?.url)
      : "";
  const pdfViewerSrc = type === "pdf" ? withPdfControls(pdfBaseSrc) : "";

  useEffect(() => {
    if (type !== "pdf") return;
    setPdfPreviewBusy(false);
    setPdfPreviewError(false);
  }, [type, pdfBaseSrc]);

  const resumeVideo = (e) => {
    if (didResume.current) return;
    const v = e.target;
    const target = Number(resumeSeconds || 0);
    if (target > 0 && (!Number.isFinite(v.duration) || target < v.duration - 2)) {
      v.currentTime = target;
    }
    didResume.current = true;
  };

  const reportVideoProgress = (e, force = false) => {
    if (!onProgress) return;
    const now = Date.now();
    if (!force && now - lastReport.current < 3000) return; // throttle frequent playback events
    lastReport.current = now;
    const v = e.target;
    onProgress({
      assetId: asset?._id,
      watchedSeconds: Math.floor(v.currentTime || 0),
      durationSeconds: Math.floor(v.duration || asset?.durationSeconds || 0),
    });
  };

  const onTimeUpdate = (e) => reportVideoProgress(e);
  const onVideoPause = (e) => reportVideoProgress(e, true);

  return (
    <div className="lms-preview-asset">
      {/* {showHeader && type !== "pdf" && (
        <div className="lms-preview-asset-head">
          <span className="lms-asset-icon">
            <i className={assetIcon(type)}></i>
          </span>
          <div className="lms-asset-main">
            <span className="lms-asset-meta">
              {t(`admin.lms.module.type_${type}`, type)}
              {formatDuration(asset?.durationSeconds)
                ? ` / ${formatDuration(asset.durationSeconds)}`
                : ""}
            </span>
            {asset?.description && (
              <span className="lms-asset-description">{asset.description}</span>
            )}
          </div>
        </div>
      )} */}

      <div className="lms-preview-media">
        {type === "video" &&
          (src ? (
            <video
              ref={videoRef}
              src={src}
              controls
              className="lms-preview-video"
              onLoadedMetadata={resumeVideo}
              onEnded={fireComplete}
              onTimeUpdate={onTimeUpdate}
              onPause={onVideoPause}
              controlsList="nodownload"
            >
              <VideoCaptionTrack asset={asset} />
            </video>
          ) : (
            <div className="lms-preview-missing">{t("admin.lms.preview.noUrl")}</div>
          ))}

        {type === "pdf" &&
          (pdfBaseSrc ? (
            <>
              <div className="lms-pdf-viewer-shell">
                <div className="lms-pdf-toolbar">
                  <div className="lms-pdf-toolbar-title">
                    <span className="lms-pdf-toolbar-icon">
                      <i className="fas fa-file-pdf"></i>
                    </span>
                    <div>
                      <strong>{asset?.title || "PDF document"}</strong>
                      <span>{t("admin.lms.module.type_pdf", "PDF")}</span>
                    </div>
                  </div>
                  <div className="lms-pdf-toolbar-actions">
                    <a
                      className={`lms-pdf-tool-btn ${!pdfBaseSrc ? "disabled" : ""}`}
                      href={pdfBaseSrc || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-disabled={!pdfBaseSrc}
                      onClick={(event) => {
                        if (!pdfBaseSrc) {
                          event.preventDefault();
                          return;
                        }
                        fireComplete();
                      }}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      {t("admin.lms.preview.openPdf", "Open PDF")}
                    </a>
                  </div>
                </div>

                <div className="lms-pdf-mobile-fallback">
                  <i className="fas fa-file-pdf" aria-hidden="true"></i>
                  <div>
                    <strong>
                      {t("admin.lms.preview.mobilePdfTitle", "Open this PDF")}
                    </strong>
                    <span>
                      {t(
                        "admin.lms.preview.mobilePdfHint",
                        "Mobile browsers may not show PDF previews inside the course player."
                      )}
                    </span>
                  </div>
                  <a
                    className={`lms-pdf-tool-btn lms-pdf-mobile-open ${!pdfBaseSrc ? "disabled" : ""}`}
                    href={pdfBaseSrc || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!pdfBaseSrc}
                    onClick={(event) => {
                      if (!pdfBaseSrc) {
                        event.preventDefault();
                        return;
                      }
                      fireComplete();
                    }}
                  >
                    <i className="fas fa-external-link-alt"></i>
                    {t("admin.lms.preview.openPdf", "Open PDF")}
                  </a>
                </div>

                <div className="lms-pdf-embed-container">
                  {pdfPreviewBusy ? (
                    <div className="lms-preview-missing">
                      {t("admin.lms.preview.loadingPdf", "Loading PDF preview...")}
                    </div>
                  ) : pdfPreviewError ? (
                    <div className="lms-preview-missing">
                      {t(
                        "admin.lms.preview.pdfPreviewFailed",
                        "PDF preview could not be opened inline."
                      )}
                    </div>
                  ) : pdfViewerSrc ? (
                    <iframe
                      src={pdfViewerSrc}
                      title={asset?.title || "pdf"}
                      width="100%"
                      height="100%"
                      onLoad={() => {
                        setPdfPreviewBusy(false);
                        fireComplete();
                      }}
                      onError={() => {
                        setPdfPreviewBusy(false);
                        setPdfPreviewError(true);
                      }}
                    />
                  ) : (
                    <div className="lms-preview-missing">{t("admin.lms.preview.noUrl")}</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="lms-preview-missing">{t("admin.lms.preview.noUrl")}</div>
          ))}

        {type === "image" &&
          (src ? (
            <img
              src={src}
              alt={asset?.title || ""}
              className="lms-preview-image"
              onLoad={fireComplete}
            />
          ) : (
            <div className="lms-preview-missing">{t("admin.lms.preview.noUrl")}</div>
          ))}

        {type === "external_url" && (
          <a
            className="btn secondary-btn"
            href={asset?.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={fireComplete}
          >
            <i className="fas fa-external-link-alt mr-1"></i>
            {t("admin.lms.preview.openResource")}
          </a>
        )}

        {type === "rich_text" && (
          <div className="lms-preview-richtext">
            {parse(DOMPurify.sanitize(asset?.content || asset?.html || ""))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LmsAssetRenderer;
